import React from 'react';
import { FiTrendingUp, FiDollarSign, FiPlus, FiTrash2 } from 'react-icons/fi';
import { generateId } from '@/utils/helpers';
import { calculateTotalCharges, calculateTotalEpargne } from '@/utils/financialCalculations';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGridThree,
  FormGroup,
  Label,
  Input,
  Select,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount,
  DynamicEntryContainer,
  EntryHeader,
  EntryTitle,
  EntryNumber,
  RemoveButton,
  AddButton,
  TextArea
} from '../common/FormStyles';
import { FinancialSituationEmployee, } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { DOCUMENTS_BY_SECTION } from '@/config/documentsBySection';
import { Candidate, Credit, Tontine } from '@/models';

interface FinancialSituationSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (sectionKey: string, docType: string, file: File) => Promise<void>;
  onDocumentRemove?: (sectionKey: string, docType: string) => void;
}

export const FinancialSituationSection: React.FC<FinancialSituationSectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleTontineChange = (index: number, field: string, value: any) => {
    const newTontines = [...(data.tontines || [])];
    newTontines[index] = { ...newTontines[index], [field]: value };
    handleChange('tontines', newTontines);
  };

  const addTontine = () => {
    const newTontine: Tontine = {
      id: generateId(),
      montant: 0,
      responsable: '',
      telephone_responsable: '',
      frequence: 'Mensuel'
    };
    handleChange('tontines', [...(data.tontines || []), newTontine]);
  };

  const removeTontine = (index: number) => {
    const newTontines = (data.tontines || []).filter((_, i) => i !== index);
    handleChange('tontines', newTontines);
  };

  const handleCreditChange = (index: number, field: string, value: any) => {
    const newCredits = [...(data.credits || [])];
    newCredits[index] = { ...newCredits[index], [field]: value };
    handleChange('credits', newCredits);
  };

  const addCredit = () => {
    const newCredit: Credit = {
      id: generateId(),
      type: 'Bancaire',
      mensualite: 0,
      mois_restants: 0
    };
    handleChange('credits', [...(data.credits || []), newCredit]);
  };

  const removeCredit = (index: number) => {
    const newCredits = (data.credits || []).filter((_, i) => i !== index);
    handleChange('credits', newCredits);
  };

  // Calculs automatiques utilisant les fonctions utilitaires centralisées
  const totalCharges = calculateTotalCharges(data);
  const totalEpargne = calculateTotalEpargne(data);

  // Fonctions wrapper pour adapter les props au format attendu par DocumentUpload
  const handleFileSelect = async (documentType: string, file: File | null) => {
    if (file && onDocumentUpload) {
      await onDocumentUpload('financial_situation', documentType, file);
    }
  };

  const handleFileRemove = (documentType: string) => {
    if (onDocumentRemove) {
      onDocumentRemove('financial_situation', documentType);
    }
  };

  // Filtrer les documents uploadés pour cette section
  const uploadedFilesForSection = uploadedDocuments.filter(doc => doc?.section === 'financial_situation');

  // Mettre à jour les totaux calculés
  React.useEffect(() => {
    if (data.total_charges !== totalCharges) {
      handleChange('total_charges', totalCharges);
    }
  }, [totalCharges]);

  React.useEffect(() => {
    if (data.total_epargne !== totalEpargne) {
      handleChange('total_epargne', totalEpargne);
    }
  }, [totalEpargne]);

  return (
    <FormSection>
      <SectionTitle>
        <FiTrendingUp />
        Situation Financière
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Renseignez vos charges mensuelles et votre épargne pour une évaluation complète
          de votre situation financière.
        </p>
      </InfoBox>

      <SubSectionTitle>Charges Mensuelles</SubSectionTitle>

      <FormGrid>
        <FormGroup>
          <Label>Loyer actuel (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.loyer_actuel || ''}
            onChange={e => handleChange('loyer_actuel', parseInt(e.target.value) || 0)}
            placeholder="Ex: 100000"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Avance locative versée</Label>
          <Input
            type="number"
            min="0"
            value={data.avance_locative_mois || ''}
            onChange={e => handleChange('avance_locative_mois', parseInt(e.target.value) || 0)}
            placeholder="Nombre de mois"
            disabled={readonly}
          />
          <HelpText>Nombre de mois d'avance versés</HelpText>
        </FormGroup>
      </FormGrid>

      <FormGridThree>
        <FormGroup>
          <Label>Électricité (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.electricite || ''}
            onChange={e => handleChange('electricite', parseInt(e.target.value) || 0)}
            placeholder="Moyenne mensuelle"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Eau (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.eau || ''}
            onChange={e => handleChange('eau', parseInt(e.target.value) || 0)}
            placeholder="Moyenne mensuelle"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Transport (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.transport || ''}
            onChange={e => handleChange('transport', parseInt(e.target.value) || 0)}
            placeholder="Ex: 30000"
            disabled={readonly}
          />
        </FormGroup>
      </FormGridThree>

      <FormGrid>
        <FormGroup>
          <Label>Scolarité enfants (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.scolarite_enfants || ''}
            onChange={e => handleChange('scolarite_enfants', parseInt(e.target.value) || 0)}
            placeholder="Total mensuel"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Soutien famille élargie (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.aide_famille || ''}
            onChange={e => handleChange('aide_famille', parseInt(e.target.value) || 0)}
            placeholder="Ex: 50000"
            disabled={readonly}
          />
        </FormGroup>
        <FormGroup>
          <Label>Autres charges (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.autres_charges || ''}
            onChange={e => handleChange('autres_charges', parseInt(e.target.value) || 0)}
            placeholder="Ex: 20000"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>


      {(data.autres_charges || 0) > 0 && (
                <FormGroup>
                  <Label>Description des autres charges</Label>
                  <TextArea
                    value={data.description_autres_charges || ''}
                    onChange={e => handleChange('description_autres_charges', e.target.value)}
                    placeholder="Ex: Électricité, eau, entretien..."
                    disabled={readonly}
                  />
                </FormGroup>
              )}

      <SubSectionTitle>Tontines</SubSectionTitle>

      {(data.tontines || []).map((tontine, index) => (
                  <DynamicEntryContainer key={tontine.id}>
                    <EntryHeader>
                      <EntryTitle>
                        <EntryNumber>{index + 1}</EntryNumber>
                        Tontine {index + 1}
                      </EntryTitle>
                      <RemoveButton
                        onClick={() => removeTontine(index)}
                        disabled={readonly}
                      >
                        <FiTrash2 />
                        Supprimer
                      </RemoveButton>
                    </EntryHeader>
      
                    <FormGrid>
                      <FormGroup>
                        <Label>Montant de la cotisation (FCFA)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={tontine.montant || ''}
                          onChange={e => handleTontineChange(index, 'montant', parseInt(e.target.value) || 0)}
                          placeholder="Ex: 10000"
                          disabled={readonly}
                        />
                      </FormGroup>
      
                      <FormGroup>
                        <Label>Fréquence</Label>
                        <Select
                          value={tontine.frequence || 'Mensuel'}
                          onChange={e => handleTontineChange(index, 'frequence', e.target.value)}
                          disabled={readonly}
                        >
                          <option value="Quotidien">Quotidien</option>
                          <option value="Hebdomadaire">Hebdomadaire</option>
                          <option value="Mensuel">Mensuel</option>
                        </Select>
                      </FormGroup>
                    </FormGrid>
      
                    <FormGroup>
                      <Label>Nom du responsable de la tontine</Label>
                      <Input
                        type="text"
                        value={tontine.responsable || ''}
                        onChange={e => handleTontineChange(index, 'responsable', e.target.value)}
                        placeholder="Nom et prénoms"
                        disabled={readonly}
                      />
                    </FormGroup>
      
                    <FormGroup>
                      <Label>Téléphone du responsable</Label>
                      <Input
                        type="tel"
                        value={tontine.telephone_responsable || ''}
                        onChange={e => handleTontineChange(index, 'telephone_responsable', e.target.value)}
                        placeholder="+221 X XX XX XX XX"
                        disabled={readonly}
                      />
                      <HelpText>
                        Ce numéro pourra être contacté pour vérifier votre participation
                      </HelpText>
                    </FormGroup>
                  </DynamicEntryContainer>
                ))}

      {!readonly && (
        <AddButton onClick={addTontine}>
          <FiPlus />
          Ajouter une tontine
        </AddButton>
      )}

      <SubSectionTitle>Crédits en Cours</SubSectionTitle>

      {(data.credits || []).map((credit, index) => (
        <DynamicEntryContainer key={credit.id}>
          <EntryHeader>
            <EntryTitle>
              <EntryNumber>{index + 1}</EntryNumber>
              Crédit {index + 1}
            </EntryTitle>
            <RemoveButton
              onClick={() => removeCredit(index)}
              disabled={readonly}
            >
              <FiTrash2 />
              Supprimer
            </RemoveButton>
          </EntryHeader>

          <FormGridThree>
            <FormGroup>
              <Label>Type de crédit</Label>
              <Select
                value={credit.type || 'Bancaire'}
                onChange={e => handleCreditChange(index, 'type', e.target.value)}
                disabled={readonly}
              >
                <option value="Bancaire">Bancaire</option>
                <option value="Microfinance">Microfinance</option>
                <option value="Informel">Informel</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Mensualité (FCFA)</Label>
              <Input
                type="number"
                min="0"
                value={credit.mensualite || ''}
                onChange={e => handleCreditChange(index, 'mensualite', parseInt(e.target.value) || 0)}
                placeholder="Ex: 50000"
                disabled={readonly}
              />
            </FormGroup>

            <FormGroup>
              <Label>Mois restants</Label>
              <Input
                type="number"
                min="0"
                value={credit.mois_restants || ''}
                onChange={e => handleCreditChange(index, 'mois_restants', parseInt(e.target.value) || 0)}
                placeholder="Ex: 24"
                disabled={readonly}
              />
            </FormGroup>
          </FormGridThree>
        </DynamicEntryContainer>
      ))}

      {!readonly && (
        <AddButton onClick={addCredit}>
          <FiPlus />
          Ajouter un crédit
        </AddButton>
      )}

      <CalculatedValue>
        <CalculatedLabel>Total charges mensuelles</CalculatedLabel>
        <CalculatedAmount>{totalCharges.toLocaleString()} FCFA</CalculatedAmount>
      </CalculatedValue>

      <SubSectionTitle>Épargne</SubSectionTitle>

      <FormGrid>
        <FormGroup>
          <Label>Compte épargne bancaire (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_bancaire || ''}
            onChange={e => handleChange('epargne_bancaire', parseInt(e.target.value) || 0)}
            placeholder="Montant disponible"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Mobile Money épargne (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_mobile_money || ''}
            onChange={e => handleChange('epargne_mobile_money', parseInt(e.target.value) || 0)}
            placeholder="Montant disponible"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label>Épargne informelle (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_informelle || ''}
            onChange={e => handleChange('epargne_informelle', parseInt(e.target.value) || 0)}
            placeholder="Ex: à la maison"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <CalculatedValue>
        <CalculatedLabel>Total épargne disponible</CalculatedLabel>
        <CalculatedAmount>{totalEpargne.toLocaleString()} FCFA</CalculatedAmount>
      </CalculatedValue>

      {onDocumentUpload && onDocumentRemove && DOCUMENTS_BY_SECTION.employee.financial_situation && DOCUMENTS_BY_SECTION.employee.financial_situation.length > 0 && (
        <>
          <SubSectionTitle>Documents Justificatifs (optionnel)</SubSectionTitle>
          <DocumentUpload
            sectionTitle="Situation Financière"
            documents={DOCUMENTS_BY_SECTION.employee.financial_situation}
            uploadedFiles={uploadedFilesForSection}
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            readonly={readonly}
          />
        </>
      )}
    </FormSection>
  );
};
